const STORAGE_KEYS = {
  token: 'x-access-token',
  user: 'user',
  business: 'business',
  branchoffice: 'branchoffice',
  branchofficeSelected: 'branchoffice_selected',
  fcmRegistration: 'smartpay_fcm_registration',
  appInstanceId: 'smartpay_android_app_instance_id',
  sessionId: 'smartpay_android_session_id',
  fcmToken: 'smartpay_android_fcm_token',
  pushEnabled: 'smartpay_android_push_enabled',
  profileId: 'smartpay_android_profile_id',
  sessionPayload: 'smartpay_android_session_payload'
};

const readJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;

    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeBoolean = (value, defaultValue = true) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  if (value === true || value === 'true' || value === '1' || value === 1) return true;
  if (value === false || value === 'false' || value === '0' || value === 0) return false;

  return defaultValue;
};

const getUserBusiness = (sessionPayload) => {
  const user = sessionPayload.user || {};
  const business = sessionPayload.business || user.business || {};

  return {
    ...business,
    id: business.id || sessionPayload.business_id || user.business_id,
    code: business.code || sessionPayload.business_code
  };
};

const getBusinessCode = (sessionPayload) => {
  const business = getUserBusiness(sessionPayload);

  return business.code || sessionPayload.business_code || '';
};

const getSessionIdentity = () => {
  const user = readJson(STORAGE_KEYS.user);
  const token = localStorage.getItem(STORAGE_KEYS.token) || '';

  return {
    token,
    user_id: user && user.id ? user.id : null,
    business_id: user && user.business ? user.business.id : user?.business_id || null,
    business_code: user && user.business ? user.business.code : null
  };
};

const clearMobilePushState = () => {
  localStorage.removeItem(STORAGE_KEYS.fcmRegistration);
};

const clearSession = (options = {}) => {
  if (typeof window !== 'undefined') {
    window.__smartpayAndroidReadyNotified = false;
  }

  localStorage.setItem(STORAGE_KEYS.token, '');
  localStorage.removeItem(STORAGE_KEYS.user);
  if (!options.preserveBusiness) {
    localStorage.removeItem(STORAGE_KEYS.business);
  }
  localStorage.removeItem(STORAGE_KEYS.branchoffice);
  localStorage.removeItem(STORAGE_KEYS.branchofficeSelected);
  localStorage.removeItem(STORAGE_KEYS.sessionId);
  localStorage.removeItem(STORAGE_KEYS.fcmToken);
  localStorage.removeItem(STORAGE_KEYS.pushEnabled);
  localStorage.removeItem(STORAGE_KEYS.profileId);
  localStorage.removeItem(STORAGE_KEYS.sessionPayload);
  clearMobilePushState();
};

const applySession = (sessionPayload = {}) => {
  if (typeof window !== 'undefined') {
    window.__smartpayAndroidReadyNotified = false;
  }

  const token = sessionPayload.token || sessionPayload.access_token;
  const user = sessionPayload.user;

  if (!token || !user) {
    return {
      success: false,
      error: 'Missing token or user'
    };
  }

  const currentIdentity = getSessionIdentity();
  const nextBusiness = getUserBusiness(sessionPayload);
  const nextUser = {
    ...user,
    business: nextBusiness,
    branchoffices: sessionPayload.branchoffices || user.branchoffices || []
  };
  const nextBusinessId = nextBusiness.id || nextUser.business_id || null;
  const nextUserId = nextUser.id || null;
  const isDifferentProfile = currentIdentity.user_id && currentIdentity.business_id &&
    (Number(currentIdentity.user_id) !== Number(nextUserId) || Number(currentIdentity.business_id) !== Number(nextBusinessId));

  if (isDifferentProfile) {
    clearMobilePushState();
    localStorage.removeItem(STORAGE_KEYS.branchoffice);
    localStorage.removeItem(STORAGE_KEYS.branchofficeSelected);
  }

  localStorage.setItem(STORAGE_KEYS.token, token);
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(nextUser));
  localStorage.setItem(STORAGE_KEYS.business, JSON.stringify(nextBusiness));
  localStorage.setItem(STORAGE_KEYS.pushEnabled, normalizeBoolean(sessionPayload.push_enabled, true) ? 'true' : 'false');
  localStorage.setItem(STORAGE_KEYS.sessionPayload, JSON.stringify({
    profile_id: sessionPayload.profile_id || null,
    business_id: nextBusinessId,
    business_code: getBusinessCode(sessionPayload),
    user_id: nextUserId,
    app_instance_id: sessionPayload.app_instance_id || null,
    session_id: sessionPayload.session_id || null,
    push_enabled: normalizeBoolean(sessionPayload.push_enabled, true)
  }));

  if (sessionPayload.profile_id) localStorage.setItem(STORAGE_KEYS.profileId, sessionPayload.profile_id);
  if (sessionPayload.app_instance_id) localStorage.setItem(STORAGE_KEYS.appInstanceId, sessionPayload.app_instance_id);
  if (sessionPayload.session_id) localStorage.setItem(STORAGE_KEYS.sessionId, sessionPayload.session_id);
  if (sessionPayload.fcm_token || sessionPayload.token_fcm) {
    localStorage.setItem(STORAGE_KEYS.fcmToken, sessionPayload.fcm_token || sessionPayload.token_fcm);
  }

  window.dispatchEvent(new CustomEvent('smartpay:android-session-applied', {
    detail: {
      token,
      user: nextUser,
      business: nextBusiness,
      business_code: getBusinessCode(sessionPayload),
      target_url: sessionPayload.target_url || null
    }
  }));

  return {
    success: true,
    business_code: getBusinessCode(sessionPayload),
    user_id: nextUserId,
    business_id: nextBusinessId
  };
};

const getCurrentSessionState = () => {
  const token = localStorage.getItem(STORAGE_KEYS.token) || '';
  const user = readJson(STORAGE_KEYS.user);
  const business = readJson(STORAGE_KEYS.business) || user?.business || null;

  return {
    has_session: Boolean(token && user),
    token,
    user,
    business,
    business_id: business?.id || user?.business_id || null,
    business_code: business?.code || null,
    app_instance_id: localStorage.getItem(STORAGE_KEYS.appInstanceId),
    session_id: localStorage.getItem(STORAGE_KEYS.sessionId),
    fcm_token: localStorage.getItem(STORAGE_KEYS.fcmToken),
    push_enabled: normalizeBoolean(localStorage.getItem(STORAGE_KEYS.pushEnabled), true),
    profile_id: localStorage.getItem(STORAGE_KEYS.profileId)
  };
};

const installAndroidSessionBridge = () => {
  if (typeof window === 'undefined') return null;

  window.SmartPayAndroidSession = {
    applySession,
    clearSession,
    getCurrentSessionState
  };

  return window.SmartPayAndroidSession;
};

const notifyAndroidDashboardReady = () => {
  if (typeof window === 'undefined') return;
  if (window.__smartpayAndroidReadyNotified) return;

  const bridge = window.AndroidBridge;
  if (bridge && typeof bridge.onDashboardReady === 'function') {
    window.__smartpayAndroidReadyNotified = true;
    bridge.onDashboardReady();
  }
};

const isPushEnabled = () => normalizeBoolean(localStorage.getItem(STORAGE_KEYS.pushEnabled), true);

const getStoredFcmToken = () => localStorage.getItem(STORAGE_KEYS.fcmToken);

const getStoredSessionId = () => localStorage.getItem(STORAGE_KEYS.sessionId);

const getStoredAppInstanceId = () => localStorage.getItem(STORAGE_KEYS.appInstanceId);

export {
  STORAGE_KEYS,
  applySession,
  clearSession,
  clearMobilePushState,
  getCurrentSessionState,
  installAndroidSessionBridge,
  isPushEnabled,
  notifyAndroidDashboardReady,
  getStoredAppInstanceId,
  getStoredFcmToken,
  getStoredSessionId
};
